/*
Copyright (C) 2009 - 2019 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import { FetchActionTypes } from 'app/oms/components/AuroraOrderView/state/actions';

/**
 * @typedef {Object} Order
 * @property {string} id - the ID of the order
 * @property {string} [customerId] - the ID of the customer who placed the order, empty for anonymous orders
 * @property {string|null} [customerName] - the name of the customer who placed the order
 * @property {string|null} [emailAddress] - the email of the customer who placed the order
 * @property {Array.<object>} orderItems - the set of order items
 * @property {string} orderNumber - the order reference number
 * @property {OrderPricing} orderPricing - the pricing information for the order
 * @property {string} status - the current status of the order
 * @property {string} submitDate - the iso-formatted date when the order was submitted
 * @property {OrderPayments} payments - the list of payments associated with this order
 * @property {Object} billingAddress - the billing address associated with this payment
 * @property {Money} coreCostTotal - the core cost total for the order
 */

/**
 * @typedef {Object} OrderPricing
 * @property {Money} adjustmentsTotal - the total price of the adjustments on the order
 * @property {Money} fulfillmentTotal - the total price of fulfillment on the order
 * @property {Money} totalTax - the total price of taxes on the order
 * @property {Money} subtotal - the sub-total price of items on the order
 * @property {Money} total - the total of the order
 */

/**
 * @typedef {Object} OrderPayments
 * @property {string} type - the type of this payment like Credit Card or Gift Card
 * @property {string} gatewayType - the gateway used to process this payment
 * @property {Money} amount - the amount for which this payment is allotted
 * @property {Array.<PaymentTransaction>} transactions -  Transaction data representing each payment gateway interaction related to this payment
 */

/**
 * @typedef {Object} PaymentTransaction
 * @property {string} type - the type of this transaction (authorize, capture, refund, etc.)
 * @property {string} status - the status of the transaction
 * @property {Money} amount - the amount related to this transaction
 */

/**
 * @typedef {Object} Money
 * @property {string} currency - the currency code
 * @property {number} amount - the decimal amount
 */

/**
 * @typedef {Object} OrderLocalState
 * @property {Order|null} data=null - the order data if fetched
 * @property {string|null} fetchError=null - the error message if a fetch error occurred
 * @property {boolean} isFetching=false - whether or not order data is being fetched
 * @property {boolean} isFetchError=false - whether or not a fetch error has occurred
 * @property {boolean} isFetchQueued=false - whether or not the fetch lifecycle is queued to execute
 * @property {number|null} lastUpdated=null - the last time the fetched data was updated
 */

/**
 * @type {OrderLocalState}
 */
export const defaultState = {
  data: null,
  fetchError: null,
  isFetching: false,
  isFetchError: false,
  isFetchQueued: false,
  lastUpdated: null
};

/**
 * Reducer used for managing the local order state.
 *
 * @param {OrderLocalState} [state] the current state
 * @param {{ type: string, [payload]: * }} action the action to reduce
 * @returns {OrderLocalState} the next state
 */
export default function defaultReducer(state = defaultState, action) {
  switch (action.type) {
    case FetchActionTypes.QUEUE_FETCH: {
      return {
        ...state,
        isFetchQueued: true
      };
    }
    case FetchActionTypes.START_FETCH: {
      return {
        ...state,
        isFetching: true,
        isFetchQueued: false
      };
    }
    case FetchActionTypes.SUCCESS_FETCH: {
      return {
        ...state,
        data: action.payload.data,
        fetchError: null,
        isFetching: false,
        isFetchError: false,
        lastUpdated: action.payload.receivedAt
      };
    }
    case FetchActionTypes.FAILURE_FETCH: {
      return {
        ...state,
        fetchError: action.payload,
        isFetching: false,
        isFetchError: true
      };
    }
    default:
      return state;
  }
}
